import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { ApiError, type Appearance, type HistoricoOuro, type LojaOfertaJogo, type LojaPedido, type LojaVitrine, type PackItem, type Sex, type VipConta } from '../api/types';
import { etagAtual } from '../data/mundoSync';
import { idiomaAtual, numero, t } from '../i18n/idioma';
import { useFechar } from '../input/useAtalhos';
import { LojaProvador, MontariaViva } from './LojaProvador';
import { iconePara, PILE_OURO } from '../assets/items';
import { IcBencao, IcCofre, IcCoroa, IcMaisGente, IcPena, IcVida } from './icones/IconesV3';
import { overlayDoPack } from '../world/spriteAtlas';
import { AbaOuro } from './LojaOuro';
import { MONTARIA_PADRAO } from '../world/montarias';
import { arteDoPergaminho } from '../world/pergaminho';

/** Pergaminho (techspec-pergaminhos-loja): usa na compra, neste personagem, sem item. */
export const ehPergaminho = (o: LojaOfertaJogo) => o.entrega.tipo === 'pergaminho_xp' || o.entrega.tipo === 'pergaminho_fadiga';

/** O preço da próxima compra: base + acréscimo × compras de hoje, até o teto (o servidor confere o mesmo). */
export function precoDoDia(o: LojaOfertaJogo, feitas: number): number {
  const acr = o.acrescimoDia ?? 0;
  if (!(acr > 0)) return o.preco;
  const p = o.preco + acr * Math.max(0, feitas);
  return o.precoTetoDia != null ? Math.min(o.precoTetoDia, p) : p;
}

function texto(f: { pt: string; en?: string; es?: string } | undefined): string {
  if (!f) return '';
  const id = idiomaAtual();
  if (id === 'en-US') return f.en || f.pt;
  if (id === 'es-ES') return f.es || f.pt;
  return f.pt;
}

function naJanela(o: LojaOfertaJogo, agora: number): boolean {
  const de = o.aVendaDe ? Date.parse(o.aVendaDe) : null;
  const ate = o.aVendaAte ? Date.parse(o.aVendaAte) : null;
  if (de != null && !Number.isNaN(de) && agora < de) return false;
  if (ate != null && !Number.isNaN(ate) && agora > ate) return false;
  return true;
}

type Modal =
  | { tipo: 'compra'; oferta: LojaOfertaJogo }
  | { tipo: 'como'; oferta: LojaOfertaJogo }
  | { tipo: 'historico' };

export function LojaGump({
  active, abaInicial, nome, shardId, personagemId, pele, peleHue, morto, onOuro, onMontar, onChanged
}: {
  active: boolean;
  abaInicial?: string | null;
  nome: string;
  shardId: string;
  personagemId: string;
  pele: string;
  peleHue: number;
  morto: boolean;
  onOuro(ouro: number): void;
  onMontar?(defId: string): void;
  onChanged(texto: string): void;
}) {
  const [vitrine, setVitrine] = useState<LojaVitrine | null>(null);
  const [aba, setAba] = useState<string | null>(null);
  const [ouro, setOuro] = useState<number | null>(null);
  const [ouroMochila, setOuroMochila] = useState(0);
  const [itens, setItens] = useState<readonly PackItem[]>([]);
  const [aparencia, setAparencia] = useState<Appearance | null>(null);
  const [sexo, setSexo] = useState<Sex>('male');
  const [shardNome, setShardNome] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState<Modal | null>(null);
  const [pedidos, setPedidos] = useState<readonly LojaPedido[] | null>(null);
  const [compras, setCompras] = useState<readonly HistoricoOuro[]>([]);
  const [prova, setProva] = useState<string | null>(null);
  const [vipConta, setVipConta] = useState<VipConta | null>(null);
  const [dir, setDir] = useState(2);
  const [hoje, setHoje] = useState<Readonly<Record<string, number>>>({});
  const [anda, setAnda] = useState<string | null>(null);
  const corpoRef = useRef<HTMLDivElement>(null);

  useFechar(() => setModal(null), { ativo: modal != null, modal: true });

  useEffect(() => {
    if (modal?.tipo !== 'historico') return;
    let vivo = true;
    setPedidos(null);
    setCompras([]);
    // As compras de ouro com dinheiro entram no mesmo histórico; se falharem, o resto aparece igual.
    void api.historicoOuro().then(lista => {
      if (vivo) setCompras(lista.filter(c => OURO_NO_HISTORICO.has(c.estado)));
    }).catch(() => {});
    void api.pedidosLoja().then(lista => {
      if (vivo) setPedidos(lista);
    }).catch(e => {
      if (!vivo) return;
      setPedidos([]);
      setErro(e instanceof ApiError ? e.message : t('loja.falhou'));
    });
    return () => { vivo = false; };
  }, [modal]);

  const carregar = () => {
    const etag = etagAtual() || '0';
    void api.loja(etag).then(v => {
      setVitrine(v);
      setAba(atual => atual && (atual === ABA_OURO || v.abas.some(a => a.id === atual)) ? atual : v.abas[0]?.id ?? null);
    }).catch(e => setErro(e instanceof Error ? e.message : t('loja.falhou')));
    void api.lojaHoje().then(h => setHoje(h.compras ?? {})).catch(() => {});
    void api.getPack().then(p => {
      const banco = p.ouroBanco ?? 0;
      setOuro(banco);
      setOuroMochila(p.gold ?? 0);
      setItens(p.items);
      onOuro(banco);
    }).catch(() => {});
  };

  useEffect(() => {
    if (!active) return;
    carregar();
    const ouvir = () => carregar();
    window.addEventListener('uoidle-mundo', ouvir);
    return () => window.removeEventListener('uoidle-mundo', ouvir);
  }, [active]);

  useEffect(() => {
    if (!active || !abaInicial) return;
    setAba(abaInicial);
  }, [active, abaInicial]);

  useEffect(() => {
    if (!active) return;
    let vivo = true;
    void api.vip().then(v => { if (vivo) setVipConta(v); }).catch(() => {});
    return () => { vivo = false; };
  }, [active]);

  useEffect(() => {
    if (!active || !shardId || !personagemId) return;
    let vivo = true;
    void api.getCharacter(shardId, personagemId).then(c => {
      if (!vivo) return;
      setAparencia(c.appearance);
      setSexo(c.sex);
    }).catch(() => {});
    void api.listShards().then(list => {
      if (!vivo) return;
      setShardNome(list.find(s => s.id === shardId)?.name ?? '');
    }).catch(() => {});
    return () => { vivo = false; };
  }, [active, shardId, personagemId]);

  useEffect(() => {
    const el = corpoRef.current;
    if (el) el.inert = modal != null;
  }, [modal]);

  const atual = vitrine?.abas.find(a => a.id === aba) ?? null;
  const agora = Date.now();
  const montada = itens.find(i => i.equippedSlot === 'mount' && i.defId)?.defId ?? MONTARIA_PADRAO;
  const roupa = overlayDoPack(itens) || 'robe';
  const ofertaProva = atual?.ofertas.find(o => o.sku === prova) ?? null;
  const mountId = ofertaProva?.entrega.defId || montada;
  const oque = ofertaProva ? texto(ofertaProva.nome) : (itens.find(i => i.defId === montada)?.name || t('loja.cavalo'));

  const comprar = async (oferta: LojaOfertaJogo) => {
    setBusy(true);
    setErro(null);
    try {
      const r = await api.comprarLoja({ sku: oferta.sku, preco: precoDe(oferta), chave: crypto.randomUUID() });
      setOuro(r.ouroBanco);
      onOuro(r.ouroBanco);
      setModal(null);
      onChanged(ehPergaminho(oferta)
        ? t(oferta.entrega.tipo === 'pergaminho_xp' ? 'pergaminho.usouXp' : 'pergaminho.usouFadiga',
          { nome: texto(oferta.nome), pct: oferta.entrega.pct ?? 0, ouro: numero(r.ouroBanco) })
        : t('loja.comprou', { nome: texto(oferta.nome), ouro: numero(r.ouroBanco) }));
      if (oferta.entrega.tipo === 'vip') {
        void api.vip().then(setVipConta).catch(() => {});
      }
      carregar();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : t('loja.falhou');
      setErro(msg);
      if (e instanceof ApiError && e.code === 'PrecoMudou') carregar();
    } finally {
      setBusy(false);
    }
  };

  const usar = async (oferta: LojaOfertaJogo) => {
    const def = oferta.entrega.defId;
    if (!def || morto) return;
    const item = itens.find(i => i.defId === def && i.equippedSlot !== 'mount') ?? itens.find(i => i.defId === def);
    if (!item) return;
    setBusy(true);
    setErro(null);
    try {
      const pack = await api.equipItem(item.id);
      setItens(pack.items);
      onMontar?.(def);
      onChanged(t('mochila.voceMonta'));
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : t('loja.falhou'));
    } finally {
      setBusy(false);
    }
  };

  const mostrarCoroa = async (mostrar: boolean) => {
    setVipConta(c => (c ? { ...c, mostrar } : c));
    try { await api.vipMostrar(mostrar); } catch (e) { setErro(e instanceof ApiError ? e.message : t('loja.falhou')); }
  };

  if (!vitrine) return <p className="muted">{erro ?? t('loja.abrindo')}</p>;

  const ofertaPorSku = (sku: string) => vitrine.abas.flatMap(a => a.ofertas).find(o => o.sku === sku) ?? null;
  const feitasHoje = (o: LojaOfertaJogo) => hoje[o.sku] ?? 0;
  const precoDe = (o: LojaOfertaJogo) => precoDoDia(o, feitasHoje(o));
  const esgotado = (o: LojaOfertaJogo) => o.limiteDia != null && feitasHoje(o) >= o.limiteDia;
  const cofre = ouro ?? 0;
  const naAbaOuro = aba === ABA_OURO;
  const tem = (o: LojaOfertaJogo) => !!o.entrega.defId && itens.some(i => i.defId === o.entrega.defId);
  const pedir = (o: LojaOfertaJogo) => { setErro(null); setModal({ tipo: precoDe(o) > cofre ? 'como' : 'compra', oferta: o }); };
  const botao = (o: LojaOfertaJogo) => {
    const aberta = vitrine.aberta && naJanela(o, agora);
    if (tem(o) && o.entrega.tipo === 'item') {
      return <button type="button" className="btn btn--tiny" disabled={busy || morto} onClick={e => { e.stopPropagation(); void usar(o); }}>{t('loja.montar')}</button>;
    }
    if (esgotado(o)) return <button type="button" className="btn btn--tiny" disabled>{t('pergaminho.esgotado')}</button>;
    const falta = precoDe(o) - cofre;
    return falta > 0
      ? <button type="button" className="btn btn--tiny" disabled={!aberta} onClick={e => { e.stopPropagation(); pedir(o); }}>{t('loja.faltamN', { n: numero(falta) })}</button>
      : <button type="button" className="btn btn--tiny btn--primary" disabled={!aberta || busy} onClick={e => { e.stopPropagation(); pedir(o); }}>{t('loja.comprar')}</button>;
  };
  const DIRS = ['loja.dir.frente', 'loja.dir.frenteEsq', 'loja.dir.esq', 'loja.dir.costasEsq', 'loja.dir.costas', 'loja.dir.costasDir', 'loja.dir.dir', 'loja.dir.frenteDir'];
  const iconeAba = (a: { id: string; tipo: string }) => a.id === ABA_OURO ? <img src={PILE_OURO} alt="" />
    : a.tipo === 'vip' ? <IcCoroa /> : atualTemMontaria(a.id) ? <img src={iconePara(MONTARIA_PADRAO)} alt="" /> : <IcCofre />;
  function atualTemMontaria(id: string) {
    return !!vitrine?.abas.find(x => x.id === id)?.provador;
  }

  return (
    <>
      <div className="lj-topo">
        <nav className="lj-abas" role="tablist" aria-label={t('loja.titulo')}>
          {[...vitrine.abas.map(a => ({ id: a.id, tipo: a.tipo, nome: texto(a.nome) })), { id: ABA_OURO, tipo: 'ouro', nome: t('loja.abaOuro') }].map(a => (
            <button key={a.id} type="button" role="tab" aria-selected={aba === a.id} aria-pressed={aba === a.id} className="loja-aba"
              onClick={() => { setAba(a.id); setProva(null); setModal(null); }}>
              {iconeAba(a)}{a.nome}
            </button>
          ))}
        </nav>
        <span className="lj-cofre" title={t('loja.cofreDica', { nome })}>
          <img src={PILE_OURO} alt="" /><b>{ouro == null ? '…' : numero(ouro)}</b><small>{t('loja.noCofre')}</small>
          <button type="button" className="lj-mais" onClick={() => { setAba(ABA_OURO); setModal(null); }}>{t('loja.maisOuro')}</button>
        </span>
      </div>
      <div className="lj-corpo" ref={corpoRef} role="tabpanel">
        {!vitrine.aberta && <p className="notice" role="status">{texto(vitrine.textos.fechada)}</p>}
        {erro && !modal && <p className="notice" role="alert">{erro}</p>}
        {naAbaOuro ? (
          <AbaOuro nome={nome} shard={shardNome} onPago={n => { carregar(); onChanged(t('pag.pagoToast', { ouro: numero(n) })); }} />
        ) : atual?.provador ? (
          <div className="lj-mont">
            <div className="lj-prov">
              <h4>{t('loja.provador')}</h4>
              <div className="lj-palco">
                <LojaProvador
                  soCena
                  nome={nome}
                  oque={oque}
                  mountId={mountId}
                  pele={pele}
                  peleHue={peleHue}
                  aparencia={aparencia}
                  sexo={sexo}
                  roupa={roupa}
                  dir={dir}
                  experimentando={!!ofertaProva}
                  onGirar={passo => setDir(d => (d + passo + 8) % 8)}
                  onVoltar={() => setProva(null)}
                />
              </div>
              <div className="lj-girar">
                <button type="button" aria-label={t('loja.girarEsq')} onClick={() => setDir(d => (d + 7) % 8)}>◀</button>
                <span>{t(DIRS[(dir + 6) % 8])}</span>
                <button type="button" aria-label={t('loja.girarDir')} onClick={() => setDir(d => (d + 1) % 8)}>▶</button>
              </div>
              <div className="lj-prov__n">
                {oque}
                <small>{ofertaProva ? t('loja.experimentandoV3') : t('loja.comoEstaV3', { nome })}</small>
              </div>
              {ofertaProva && (tem(ofertaProva) ? botao(ofertaProva) : (
                <button type="button" className={`btn${ofertaProva.preco <= cofre ? ' btn--primary' : ''}`} disabled={!vitrine.aberta} onClick={() => pedir(ofertaProva)}>
                  {ofertaProva.preco <= cofre
                    ? t('loja.comprarOuro', { n: numero(ofertaProva.preco) })
                    : t('loja.faltamOuro', { n: numero(ofertaProva.preco - cofre) })}
                </button>
              ))}
            </div>
            <div className="lj-vit">
              {atual.intro && <p className="lj-intro"><IcBencao />{texto(atual.intro)} {t('loja.cliqueExperimentar')}</p>}
              {atual.ofertas.length === 0 && <p className="muted">{t('loja.vazia')}</p>}
              <div className="lj-grade">
                {atual.ofertas.map(o => {
                  const ja = tem(o);
                  const def = o.entrega.defId ?? '';
                  return (
                    <button key={o.sku} type="button" className="lj-card loja-cartao" aria-pressed={prova === o.sku} title={texto(o.descricao)}
                      onPointerEnter={() => setAnda(o.sku)} onPointerLeave={() => setAnda(x => (x === o.sku ? null : x))}
                      onClick={() => setProva(x => (x === o.sku ? null : o.sku))}>
                      <span className="lj-card__selo"><Selo o={o} tem={ja} /></span>
                      <span className="lj-card__arte">{def && <MontariaViva id={def} anda={anda === o.sku || prova === o.sku} />}</span>
                      <span className="lj-card__n">{texto(o.nome)}</span>
                      <span className="lj-card__p">
                        {ja ? <small>{t('loja.naMochila')}</small> : <>
                          <Ouro n={o.preco} />
                          {o.preco > cofre && <small className="is-falta">{t('loja.faltamN', { n: numero(o.preco - cofre) })}</small>}
                        </>}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : atual?.tipo === 'vip' || atual?.id === 'vip' ? (
          <div className="lj-vip">
            <div className="lj-vip__st">
              <IcCoroa />
              <div>
                <h3>{vipConta && vipConta.ate > agora ? t('loja.vipAte', { data: dataDe(vipConta.ate) }) : t('loja.naoVip')}</h3>
                <ul>
                  <li><IcCoroa />{t('loja.vipCoroa')}</li>
                  <li><IcVida />{t('loja.vipPocao')}</li>
                </ul>
              </div>
              <button type="button" className="lei-tg" aria-pressed={vipConta?.mostrar !== false} onClick={() => void mostrarCoroa(vipConta?.mostrar === false)}>
                <span className="cb-sw" aria-pressed={vipConta?.mostrar !== false} />{t('loja.mostrarCoroa')}
              </button>
            </div>
            <div className="lj-planos">
              {atual.ofertas.map(o => {
                const d = diasDaOferta(o);
                const base = Math.max(agora, vipConta?.ate ?? 0);
                return (
                  <div key={o.sku} className={`lj-plano${o.selo === 'destaque' ? ' is-destaque' : ''}`}>
                    {o.selo === 'destaque' && <span className="lj-selo lj-selo--destaque">{t('loja.maisEscolhido')}</span>}
                    <span className="lj-plano__d">{d}<small>{t('loja.dias')}</small></span>
                    <Ouro n={o.preco} />
                    {d > 0 && <span className="lj-plano__por">{t('loja.ouroPorDia', { n: numero(Math.round(o.preco / d)) })}</span>}
                    {d > 0 && <span className="lj-plano__ate">{t((vipConta?.ate ?? 0) > agora ? 'loja.vipVaiAte' : 'loja.vipAteData', { data: dataDe(base + d * 86_400_000) })}</span>}
                    {botao(o)}
                  </div>
                );
              })}
            </div>
          </div>
        ) : atual && atual.ofertas.length > 0 && atual.ofertas.every(ehPergaminho) ? (
          <>
            {atual.intro && <p className="lj-intro">{texto(atual.intro)}</p>}
            <div className="lj-grade lj-perg">
              {atual.ofertas.map(o => {
                const n = feitasHoje(o);
                const preco = precoDe(o);
                const acabou = esgotado(o);
                return (
                  <button key={o.sku} type="button" className="lj-card loja-cartao lj-perg__card" title={texto(o.descricao)}
                    disabled={!vitrine.aberta || !naJanela(o, agora) || acabou} onClick={() => pedir(o)}>
                    <span className="lj-card__selo">
                      {n > 0 && (o.acrescimoDia ?? 0) > 0 ? <span className="lj-selo lj-selo--prazo">{t('pergaminho.nHoje', { n })}</span> : <Selo o={o} tem={false} />}
                    </span>
                    <span className="lj-card__arte"><ArtePergaminho o={o} brilho /></span>
                    <span className="lj-card__n">{texto(o.nome)}</span>
                    <span className="lj-perg__d">{texto(o.descricao)}</span>
                    <span className="lj-card__p">
                      <Ouro n={preco} />
                      {acabou ? <small className="is-falta">{t('pergaminho.esgotadoHoje')}</small>
                        : (o.acrescimoDia ?? 0) > 0 ? <small className="is-sobe">{t('pergaminho.sobe', { n: numero(o.acrescimoDia ?? 0) })}</small>
                          : preco > cofre ? <small className="is-falta">{t('loja.faltamN', { n: numero(preco - cofre) })}</small> : null}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        ) : atual ? (
          <>
            {atual.intro && <p className="lj-intro">{texto(atual.intro)}</p>}
            {atual.ofertas.length === 0 && <p className="muted">{t('loja.vazia')}</p>}
            <div className="lj-svs">
              {atual.ofertas.map(o => {
                const Ic = o.entrega.tipo === 'vaga_personagem' ? IcMaisGente : o.entrega.tipo === 'troca_nome' ? IcPena : IcCofre;
                return (
                  <div key={o.sku} className="lj-sv">
                    <span className="lj-sv__ic"><Ic /></span>
                    <span><b>{texto(o.nome)}</b><small>{texto(o.descricao)}{o.limitePorPersonagem ? ` · ${t('loja.limitePor', { n: o.limitePorPersonagem })}` : ''}</small></span>
                    <Ouro n={precoDe(o)} />
                    {botao(o)}
                  </div>
                );
              })}
            </div>
          </>
        ) : null}
      </div>
      <div className="lj-pe">
        <IcBencao />
        <span>{t('loja.peTudo', { nome, shard: shardNome || '' })}</span>
        <button type="button" onClick={() => { setErro(null); setModal({ tipo: 'historico' }); }}>{t('loja.historico')}</button>
      </div>
      {modal && (
        <LojaModal
          modal={modal}
          nome={nome}
          ouro={cofre}
          ouroMochila={ouroMochila}
          busy={busy}
          erro={erro}
          pedidos={pedidos}
          compras={compras}
          nomeDoSku={sku => (ofertaPorSku(sku) ? texto(ofertaPorSku(sku)!.nome) : sku)}
          precoDe={precoDe}
          feitasHoje={feitasHoje}
          temAbaOuro
          onFechar={() => setModal(null)}
          onConfirmar={oferta => void comprar(oferta)}
          onIrOuro={() => { setAba(ABA_OURO); setModal(null); }}
        />
      )}
    </>
  );
}

const ABA_OURO = 'ouro';

function Ouro({ n }: { n: number }) {
  return <span className="lj-ouro"><img src={PILE_OURO} alt="" />{numero(n)}</span>;
}

function Selo({ o, tem }: { o: LojaOfertaJogo; tem: boolean }) {
  if (tem) return <span className="lj-selo lj-selo--tem">{t('loja.voceTem')}</span>;
  if (o.aVendaAte) {
    const d = new Date(o.aVendaAte);
    if (!Number.isNaN(d.getTime())) return <span className="lj-selo lj-selo--prazo">{t('loja.ate', { data: d.toLocaleDateString(idiomaAtual(), { day: '2-digit', month: '2-digit' }) })}</span>;
  }
  if (o.selo === 'destaque') return <span className="lj-selo lj-selo--destaque">{t('loja.destaque')}</span>;
  if (o.selo === 'novo') return <span className="lj-selo lj-selo--novo">{t('loja.novo')}</span>;
  return null;
}

/** As compras de ouro que contam no histórico: as que cobraram (ou ainda podem cobrar). Cancelado, vencido e falhou não. */
const OURO_NO_HISTORICO = new Set(['aguardando', 'processando', 'pago', 'reembolsado', 'contestado', 'perdido', 'sem_destino']);

const reaisHist = (c: number) => (c / 100).toLocaleString(idiomaAtual(), { style: 'currency', currency: 'BRL' });

function quando(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(idiomaAtual(), { dateStyle: 'short', timeStyle: 'short' });
}

function LojaModal({
  modal, nome, ouro, ouroMochila, busy, erro, pedidos, compras, nomeDoSku, precoDe, feitasHoje, temAbaOuro, onFechar, onConfirmar, onIrOuro
}: {
  modal: Modal;
  nome: string;
  ouro: number;
  ouroMochila: number;
  busy: boolean;
  erro: string | null;
  pedidos: readonly LojaPedido[] | null;
  compras: readonly HistoricoOuro[];
  nomeDoSku(sku: string): string;
  precoDe(o: LojaOfertaJogo): number;
  feitasHoje(o: LojaOfertaJogo): number;
  temAbaOuro: boolean;
  onFechar(): void;
  onConfirmar(oferta: LojaOfertaJogo): void;
  onIrOuro(): void;
}) {
  const caixa = useRef<HTMLDivElement>(null);
  useEffect(() => {
    caixa.current?.querySelector<HTMLElement>('button.btn--primary, button')?.focus();
  }, [modal]);

  const prender = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const root = caixa.current;
    if (!root) return;
    const lista = [...root.querySelectorAll<HTMLElement>('button, [href], input')].filter(el => !el.hasAttribute('disabled'));
    if (lista.length === 0) return;
    const i = lista.indexOf(document.activeElement as HTMLElement);
    const prox = e.shiftKey ? (i <= 0 ? lista.length - 1 : i - 1) : (i === lista.length - 1 || i < 0 ? 0 : i + 1);
    e.preventDefault();
    lista[prox]?.focus();
  };

  let miolo: React.ReactNode;
  if (modal.tipo === 'compra') {
    const o = modal.oferta;
    const preco = precoDe(o);
    const depois = ouro - preco;
    const perg = ehPergaminho(o);
    miolo = (
      <>
        <h3>{texto(o.nome)}</h3>
        <ArteDaOferta o={o} />
        <div className="lj-linha"><span>{t('loja.precoLinha')}</span><span className="lj-ouro"><img src={PILE_OURO} alt="" />{numero(preco)}</span></div>
        <div className="lj-linha"><span>{t('loja.cofreDepois')}</span><b>{numero(depois)}</b></div>
        {perg ? <EfeitoDoPergaminho o={o} nome={nome} feitas={feitasHoje(o)} /> : (
          <div className="lj-linha"><span>{t('loja.ficaComRot')}</span><b>{t('loja.paraSempre', { nome })}</b></div>
        )}
        {o.entrega.abencoado && (
          <div className="lj-linha"><span>{t('loja.abencoada')}</span><b>{t('loja.naoCai')}</b></div>
        )}
        {erro && <p className="notice" role="alert">{erro}</p>}
        <div className="lj-caixa__acoes">
          <button type="button" className="btn btn--ghost" disabled={busy} onClick={onFechar}>{t('comum.cancelar')}</button>
          <button type="button" className="btn btn--primary" disabled={busy} onClick={() => onConfirmar(o)}>
            {t(perg ? 'pergaminho.comprarUsar' : 'loja.comprarPor', { preco: numero(preco) })}
          </button>
        </div>
      </>
    );
  } else if (modal.tipo === 'como') {
    miolo = (
      <>
        <h3>{texto(modal.oferta.nome)}</h3>
        <ArteDaOferta o={modal.oferta} />
        <p>{t('loja.faltamNoCofre', { n: numero(precoDe(modal.oferta) - ouro) })}</p>
        {ouroMochila > 0 && <p>{t('loja.comoMochila', { ouro: numero(ouroMochila) })}</p>}
        <div className="lj-caixa__acoes">
          <button type="button" className="btn btn--ghost" onClick={onFechar}>{t('loja.voltarModal')}</button>
          {temAbaOuro && <button type="button" className="btn btn--primary" onClick={onIrOuro}>{t('loja.comprarOuroBtn')}</button>}
        </div>
      </>
    );
  } else {
    // Uma lista só, da mais nova para a mais velha: o que saiu do cofre (itens, VIP) e o ouro comprado com dinheiro.
    const linhas = pedidos === null ? null : [
      ...pedidos.map(p => ({ quando: p.criadoEm, item: (
        <li key={`l-${p.id}`}>
          {nomeDoSku(p.sku)}
          <small>{quando(p.criadoEm)}{p.estado === 'estornado' ? ` · ${t('loja.estornado')}` : ''}</small>
          <b><span className="lj-ouro"><img src={PILE_OURO} alt="" />{numero(p.precoOuro)}</span></b>
        </li>
      ) })),
      ...compras.map(c => ({ quando: c.criadoEm, item: (
        <li key={`o-${c.id}`} className="lj-hist__ouro">
          <span className="lj-ouro"><img src={PILE_OURO} alt="" />{t('loja.histOuro', { n: numero(c.ouro) })}</span>
          <small>{quando(c.criadoEm)} · {c.metodo === 'pix' ? 'Pix' : t('loja.histCartao')}{c.estado !== 'pago' ? ` · ${t(`loja.histEstado.${c.estado}`)}` : ''}</small>
          <b>{reaisHist(c.centavos)}</b>
        </li>
      ) }))
    ].sort((a, b) => Date.parse(b.quando) - Date.parse(a.quando));
    miolo = (
      <>
        <h3>{t('loja.historico')}</h3>
        {erro && <p className="notice" role="alert">{erro}</p>}
        {!erro && linhas === null && <p className="muted">{t('comum.carregando')}</p>}
        {!erro && linhas?.length === 0 && <p className="muted">{t('loja.historicoVazio')}</p>}
        {!erro && linhas && linhas.length > 0 && <ul className="lj-hist">{linhas.map(l => l.item)}</ul>}
        <button type="button" className="btn btn--primary" onClick={onFechar}>{t('comum.fechar')}</button>
      </>
    );
  }

  return (
    <div className="lj-modal" onKeyDown={prender} onClick={e => { if (e.target === e.currentTarget) onFechar(); }}>
      <div className="lj-caixa" role="dialog" aria-modal="true" ref={caixa}>{miolo}</div>
    </div>
  );
}

/** A arte do pergaminho: a do cliente da UO; na loja, com o brilho na cor dele (techspec-pergaminhos-loja §04). */
function ArtePergaminho({ o, brilho }: { o: LojaOfertaJogo; brilho?: boolean }) {
  const src = arteDoPergaminho(o.entrega.arte);
  const cor = o.brilho && /^#[0-9a-f]{6}$/i.test(o.brilho) ? o.brilho : '#e6c452';
  const estilo = brilho ? ({ '--brilho': cor, '--mascara': `url(${src})` } as React.CSSProperties) : undefined;
  return (
    <span className={`lj-perg__arte${brilho ? ' is-brilho' : ''}`} style={estilo}>
      <img src={src} alt="" draggable={false} />
    </span>
  );
}

/** O que o pergaminho faz, na caixa de compra, e a escada de preço do dia (fadiga). */
function EfeitoDoPergaminho({ o, nome, feitas }: { o: LojaOfertaJogo; nome: string; feitas: number }) {
  const pct = o.entrega.pct ?? 0;
  if (o.entrega.tipo === 'pergaminho_xp') {
    const min = o.entrega.minutos ?? 0;
    const teto = o.entrega.tetoMinutos ?? 0;
    return (
      <>
        <div className="lj-linha"><span>{t('pergaminho.efeito')}</span><b>{t('pergaminho.xpEfeito', { pct, min })}</b></div>
        <p className="lj-aviso">{t('pergaminho.xpRegra', { teto: Math.round(teto / 60) })}</p>
        <div className="lj-linha"><span>{t('loja.ficaComRot')}</span><b>{t('pergaminho.usaEm', { nome })}</b></div>
      </>
    );
  }
  const acr = o.acrescimoDia ?? 0;
  const ate = o.limiteDia ?? Math.max(6, feitas + 3);
  const degraus = Array.from({ length: Math.min(ate, 12) }, (_, i) => i);
  return (
    <>
      <div className="lj-linha"><span>{t('pergaminho.efeito')}</span><b>{t('pergaminho.fadigaEfeito', { pct })}</b></div>
      <div className="lj-linha"><span>{t('loja.ficaComRot')}</span><b>{t('pergaminho.usaEm', { nome })}</b></div>
      {acr > 0 && (
        <>
          <div className="lj-escada" aria-label={t('pergaminho.escada')}>
            {degraus.map(i => (
              <span key={i} className={`lj-degrau${i < feitas ? ' is-pago' : i === feitas ? ' is-agora' : ''}`}>
                <small>{t('pergaminho.compraN', { n: i + 1 })}</small>{numero(precoDoDia(o, i))}
              </span>
            ))}
          </div>
          <p className="lj-aviso">{t(o.limiteDia ? 'pergaminho.zeraLimite' : 'pergaminho.zera', { base: numero(o.preco), n: o.limiteDia ?? 0 })}</p>
        </>
      )}
    </>
  );
}

/** A arte da oferta na caixa: a montaria parada, a coroa do VIP ou o ícone do serviço. */
function ArteDaOferta({ o }: { o: LojaOfertaJogo }) {
  if (ehPergaminho(o)) return <div className="lj-caixa__arte"><ArtePergaminho o={o} brilho /></div>;
  const Ic = o.entrega.tipo === 'vip' ? IcCoroa : o.entrega.tipo === 'vaga_personagem' ? IcMaisGente : o.entrega.tipo === 'troca_nome' ? IcPena : IcCofre;
  return <div className="lj-caixa__arte">{o.entrega.defId ? <MontariaViva id={o.entrega.defId} anda={false} /> : <Ic />}</div>;
}

function dataDe(ms: number): string {
  return new Date(ms).toLocaleDateString(idiomaAtual());
}

function diasDaOferta(o: LojaOfertaJogo): number {
  if (o.entrega.dias && o.entrega.dias > 0) return o.entrega.dias;
  const m = /^vip-(\d+)$/.exec(o.sku);
  return m ? Number(m[1]) : 0;
}
